/**
 * What is wrong on the form, per field name. The rules come from the browser's own
 * constraint validation, already declared on the controls; this owns only the wording
 * and the moment, as the page suppresses the browser's bubbles.
 */

export type FieldErrors = Record<string, string>;

type ValidatableControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

function messageFor(control: ValidatableControl, label: string) {
  const { validity } = control;

  if (validity.valueMissing) {
    return `${label} is required`;
  }

  if (validity.typeMismatch && control.type === "email") {
    return `${label} must be a valid email address`;
  }

  // Everything else keeps the browser's own wording rather than paraphrasing it.
  return control.validationMessage || `${label} is not valid`;
}

export function collectFieldErrors(
  form: HTMLFormElement | null | undefined,
  labels: Record<string, string> = {}
): FieldErrors {
  const errors: FieldErrors = {};

  if (!form) {
    return errors;
  }

  Array.from(form.elements).forEach((element) => {
    const control = element as ValidatableControl;

    if (!control.name || !control.willValidate || control.validity?.valid) {
      return;
    }

    // A radio group is several controls under one name; the first says it.
    if (errors[control.name]) {
      return;
    }

    errors[control.name] = messageFor(control, labels[control.name] ?? control.name);
  });

  return errors;
}

/**
 * Puts the user on the first thing they have to fix. Named rather than found by
 * `:invalid`, so the order matches the messages.
 */
export function focusFirstError(
  form: HTMLFormElement | null | undefined,
  errors: FieldErrors
) {
  const [first] = Object.keys(errors);

  if (!form || !first) {
    return;
  }

  const control = form.elements.namedItem(first);

  // A radio group comes back as a list of its inputs. Checked by shape, as RadioNodeList
  // is not a global in every environment this runs in.
  const element = (
    control instanceof HTMLElement
      ? control
      : (control as unknown as ArrayLike<HTMLElement> | null)?.[0]
  ) as HTMLElement | undefined;

  element?.focus?.();
  element?.scrollIntoView?.({ block: "center" });
}
